import React, { createContext, useContext, useRef, useCallback } from 'react';
import { UserPermissions, ResourceType } from '../api/userPermissions';

/**
 * 用户权限缓存 Context
 * 管理所有资源的权限数据缓存
 */
interface UserPermissionsContextType {
  // 获取权限
  getPermissions: (resourceType: ResourceType, resourceID: string) => UserPermissions[] | undefined;
  // 设置权限
  setPermissions: (resourceType: ResourceType, resourceID: string, permissions: UserPermissions[]) => void;
  // 清除某个资源的权限
  clearPermissions: (resourceType: ResourceType, resourceID: string) => void;
  // 清除某个资源类型的所有权限
  clearPermissionsByResourceType: (resourceType: ResourceType) => void;
  // 清除所有权限
  clearAllPermissions: () => void;
  // 检查是否有缓存
  hasPermissions: (resourceType: ResourceType, resourceID: string) => boolean;
}

const UserPermissionsContext = createContext<UserPermissionsContextType | undefined>(undefined);

export const useUserPermissions = () => {
  const context = useContext(UserPermissionsContext);
  if (context === undefined) {
    throw new Error('useUserPermissions must be used within a UserPermissionsProvider');
  }
  return context;
};

/**
 * 生成缓存key
 */
const getCacheKey = (resourceType: ResourceType, resourceID: string): string => {
  return `${resourceType}_${resourceID}`;
};

export const UserPermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 使用 ref 存储缓存，避免触发重新渲染
  // Key格式: resourceType_resourceID
  const cacheRef = useRef<Map<string, UserPermissions[]>>(new Map());

  const getPermissions = useCallback(
    (resourceType: ResourceType, resourceID: string): UserPermissions[] | undefined => {
      const key = getCacheKey(resourceType, resourceID);
      return cacheRef.current.get(key);
    },
    []
  );

  const setPermissions = useCallback(
    (resourceType: ResourceType, resourceID: string, permissions: UserPermissions[]) => {
      const key = getCacheKey(resourceType, resourceID);
      cacheRef.current.set(key, permissions);
    },
    []
  );

  const clearPermissions = useCallback((resourceType: ResourceType, resourceID: string) => {
    const key = getCacheKey(resourceType, resourceID);
    cacheRef.current.delete(key);
  }, []);

  const clearPermissionsByResourceType = useCallback((resourceType: ResourceType) => {
    const keysToDelete: string[] = [];
    cacheRef.current.forEach((_, key) => {
      if (key.startsWith(`${resourceType}_`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => cacheRef.current.delete(key));
  }, []);

  const clearAllPermissions = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  const hasPermissions = useCallback(
    (resourceType: ResourceType, resourceID: string): boolean => {
      const key = getCacheKey(resourceType, resourceID);
      return cacheRef.current.has(key);
    },
    []
  );

  const value: UserPermissionsContextType = {
    getPermissions,
    setPermissions,
    clearPermissions,
    clearPermissionsByResourceType,
    clearAllPermissions,
    hasPermissions,
  };

  return <UserPermissionsContext.Provider value={value}>{children}</UserPermissionsContext.Provider>;
};
